import React, { useEffect, useState, useRef } from 'react';
import { Check, Image, MessageCircle, RefreshCw, SquarePlus, Flag } from 'lucide-react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from './ui/alert-dialog';
import { getDisplayName, getMediaUrl, getStoragePathFromUrl } from '../lib/utils';
import type { Database } from '../types/database';
import { supabaseEh } from '../lib/supabase';
import { CHARACTER_EVENTS, useTracking } from '@/hooks/useTracking';
import { EhFlagDialog } from './EhFlagDialog';

type Character = Database['eh']['Tables']['characters']['Row'];

interface CharacterCreationSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  character: Character | null;
  imageStatus: 'idle' | 'pending' | 'success' | 'failed';
  avatarUrl: string | null;
  onCharacterSave: () => void;
  onStartChat: () => void;
  onRetryImage: (customPrompt: string, resourceId: string, { requestId, successfulGenCount }: {
    requestId?: string,
    successfulGenCount?: number
  }) => Promise<{ success: boolean, userErrorMsg?: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  userAttributes: Record<string, any>;
}

interface CharacterAttributeProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

const CharacterAttribute: React.FC<CharacterAttributeProps> = ({ label, value, className }) => (
  <div className={`flex flex-col ${className || ''}`}>
    <span className="text-xs text-zinc-400 mb-1">{label}</span>
    <div className={`bg-zinc-800/50 rounded-lg px-3 py-2 text-white text-sm min-h-[36px] line-clamp-2 ${
      value && value.toString().split(' ').length < 5 ? 'capitalize' : ''
    }`}>
      {value || <span className="text-zinc-500">—</span>}
    </div>
  </div>
);

export const CharacterCreationSuccessDialog: React.FC<CharacterCreationSuccessDialogProps> = ({
  open,
  onOpenChange,
  character,
  imageStatus,
  avatarUrl,
  onCharacterSave,
  onStartChat,
  onRetryImage,
  userAttributes,
}) => {
  const { trackEvent } = useTracking();

  const [firstName, setFirstName] = useState(character?.first_name || '');
  const [lastName, setLastName] = useState(character?.last_name || '');
  const [editing, setEditing] = useState(true);
  const [hasEdited, setHasEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [showFlagDialog, setShowFlagDialog] = useState(false);

  const galleryRef = useRef<HTMLDivElement>(null);

  // Reset name editing state when dialog opens again (changing characters)
  useEffect(() => {
    if (open && character) {
      setFirstName(character.first_name || '');
      setLastName(character.last_name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && avatarUrl && !generatedImages.includes(avatarUrl)) {
      setGeneratedImages(prev => [...prev, avatarUrl]);
      setSelectedImage(avatarUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarUrl, open]);

  // Scroll gallery to right when a new image is added
  useEffect(() => {
    if (galleryRef.current && generatedImages.length > 1) {
      galleryRef.current.scrollTo({
        left: galleryRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  }, [generatedImages.length]);

  if (!character) return null;

  const handleUpdateName = async () => {
    if (!character || hasEdited || isSaving) return;
    setIsSaving(true);
    setError(null); 
    const { error: updateError } = await supabaseEh
      .from('characters')
      .update({ first_name: firstName, last_name: lastName })
      .eq('id', character.id);
    setIsSaving(false);
    if (updateError) {
      setError('Failed to update name.');
    } else {
      setHasEdited(true);
      setEditing(false);
      onCharacterSave();
    }
  };

  const handleRetryImage = async () => {
    if (!character) return;
    setRetrying(true);
    await handleRegenerateImage();
    setRetrying(false);
  };

  const handleRegenerateImage = async () => {
    if (!character) return;
    setError(null);
    setIsGenerating(true);

    const customPrompt = (character.char_metadata as { imagePrompt?: string })?.imagePrompt;
    if (!customPrompt) return;

    try {
      const { success, userErrorMsg } = await onRetryImage(customPrompt, character.id, {
        successfulGenCount: generatedImages.length,
      });
      if (!success) {
        setError(userErrorMsg || 'Failed to take the photo. Please try again.');
      }
    } catch (error) {
      trackEvent(CHARACTER_EVENTS.NEW_PROFILE_IMAGE_ERROR, {
        error: error instanceof Error ? error.message : 'Unknown error',
        url_slug: character.url_slug,
      });
    } finally {
      setIsGenerating(false);
    }
  }

  // Update avatar/profile_media in Supabase when selecting a new image
  const handleSelectImage = async (img: string) => {
    if (img === selectedImage || isUpdatingAvatar) return;
    setSelectedImage(img);
    setIsUpdatingAvatar(true);
    setShowSpinner(false);
    setError(null);
    // Show spinner if update takes longer than 300ms
    const spinnerTimeout = setTimeout(() => setShowSpinner(true), 300);
    const imgPath = getStoragePathFromUrl(img);
    try {
      const { error: updateError } = await supabaseEh
        .from('characters')
        .update({
          avatar_fpath: imgPath,
          profile_media_fpaths: [imgPath],
          updated_at: new Date().toISOString(),
        })
        .eq('id', character.id);
      if (updateError) setError('Oops, something went wrong. Please try again.');
      else onCharacterSave();
    } catch (e) {
      trackEvent(CHARACTER_EVENTS.NEW_PROFILE_IMAGE_ERROR, {
        error: e instanceof Error ? e.message : 'Unknown error',
        url_slug: character.url_slug,
        source: 'CharacterCreationSuccessDialog.handleSelectImage',
      })
      setError('Oops, something went wrong. Please try again.');
    } finally {
      clearTimeout(spinnerTimeout);
      setIsUpdatingAvatar(false);
      setShowSpinner(false);
    }
  };

  const getHeaderImage = () => {
    if (selectedImage) return selectedImage;
    if (avatarUrl) return avatarUrl;
    if (character.avatar_fpath) return getMediaUrl(character.avatar_fpath);
    if (character.profile_media_fpaths && character.profile_media_fpaths.length > 0) {
      return getMediaUrl(character.profile_media_fpaths[0]);
    }
    return '';
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="p-0 sm:max-w-4xl w-full h-full md:h-auto bg-zinc-900 border-none text-white overflow-y-auto">
        <div className="flex flex-col md:flex-row">
          {/* Left column - Image */}
          <div className={`max-sm:max-h-[50dvh] w-full md:w-1/2 aspect-square md:aspect-auto relative flex items-center justify-center ${
            getHeaderImage() ? 'bg-zinc-900' : 'bg-zinc-800'
          }`}>
            {error && (
              <div className="absolute inset-x-0 top-0 bg-zinc-900/60">
                <div className="text-rose-500 text-center px-6 py-2 font-medium">
                  {error}
                </div>
              </div>
            )}
            {imageStatus === 'pending' && !getHeaderImage() ? (
              <div className="flex flex-col items-center justify-center">
                <div className="w-16 h-16 border-4 border-zinc-600 border-t-pink-500 rounded-full animate-spin mb-4"></div>
                <p className="text-zinc-300 text-center px-6">
                  Taking a nice photo of {getDisplayName({ first_name: firstName, last_name: lastName })}...
                </p>
              </div>
            ) : imageStatus === 'failed' ? (
              <div className="flex flex-col items-center justify-center p-6">
                <div className="w-16 h-16 bg-zinc-700 rounded-full flex items-center justify-center mb-4">
                  <Image className="h-8 w-8 text-zinc-400" />
                </div>
                <p className="text-zinc-300 text-center mb-4">
                  Image generation failed. Please try again.
                </p>
                <Button 
                  variant="outline" 
                  className="bg-zinc-800 hover:bg-zinc-700 hover:text-white border-zinc-700"
                  onClick={handleRetryImage}
                  disabled={retrying}
                >
                  {retrying ? 'Retrying...' : 'Retry Image Generation'}
                </Button>
              </div>
            ) : (getHeaderImage() && (
              <>
                <img
                  src={getHeaderImage()}
                  alt={`${firstName} ${lastName}`}
                  className="w-full h-full object-contain sm:object-cover object-center"
                />
                {/* Gallery of thumbnails */}
                {generatedImages.length > 1 && (
                  <div className="absolute left-0 right-0 bottom-15 sm:bottom-18 flex flex-col items-center justify-center z-10">
                    <div className="text-sm text-zinc-100 rounded-t-sm pt-1 px-2 bg-zinc-900/70 font-medium select-none">
                      Pick one image
                    </div>
                    <div
                      ref={galleryRef}
                      className={'flex gap-2 overflow-x-auto p-2 bg-zinc-900/70 rounded-sm '
                        + 'max-w-full scrollbar-thumb-zinc-700 scrollbar-track-transparent'}
                      style={{ scrollbarWidth: 'thin' }}
                    >
                      {generatedImages.slice(-10).map((img, idx) => (
                        <button
                          key={img}
                          type="button"
                          className={`relative rounded-md border-2 transition-all duration-150 size-14 min-w-14 min-h-14 overflow-hidden ${
                            img === selectedImage ? 'border-pink-500' : 'border-zinc-700 hover:border-zinc-500'
                          }`}
                          onClick={() => handleSelectImage(img)}
                          disabled={isUpdatingAvatar && img === selectedImage}
                          aria-label={`Select image ${idx + 1}`}
                        >
                          <img
                            src={img}
                            alt={`Thumbnail ${idx + 1}`}
                            className="w-full h-full object-cover rounded-md"
                          />
                          {/* Spinner overlay */}
                          {isUpdatingAvatar && img === selectedImage && showSpinner && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <div className="w-6 h-6 border-2 border-zinc-400 border-t-pink-500 rounded-full animate-spin" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Button 
                  variant="pink" 
                  size="lg"
                  className="absolute inset-x-[20%] bottom-2 sm:bottom-4 opacity-85"
                  onClick={handleRegenerateImage}
                  disabled={isGenerating}
                >
                  <RefreshCw className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
                  {/* <span className={isGenerating
                    ? 'w-5 h-5 border-2 border-white border-t-pink-500 rounded-full animate-spin'
                    : 'hidden'
                  }/> */}
                  <span>{isGenerating ? 'Generating...' : 'New photo'}</span>
                </Button>
              </>
            ))}
          </div>

          {/* Right column - Character details */}
          <div className="relative w-full md:w-1/2 p-6 flex flex-col">
            <AlertDialogTitle className="text-2xl font-bold text-white mb-4 text-center flex items-center justify-center gap-2">
              <span>{`${character.gender === 'm' ? 'He' : 'She'} is all set!`}</span>
            </AlertDialogTitle>
            <button
              className="absolute top-2 right-2 flex items-center justify-center size-8 rounded-full bg-zinc-800 hover:bg-zinc-700"
              onClick={() => {
                setShowFlagDialog(true);
              }}
            >
              <Flag className="size-4 text-zinc-400" />
            </button>

            {/* Name edit section */}
            <div className="mb-2">
              <div className="text-center mb-2 text-zinc-400 text-sm">
                <span>{editing ? 'Enter a name' : 'Character name'}</span>
                {editing && <span className="ml-1 text-xs">(You won't be able to change it later)</span>}
              </div>
              {editing ? (
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <Input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value.trim())}
                      minLength={1}
                      placeholder="First name"
                      className="bg-zinc-800 border-zinc-700"
                      disabled={isSaving}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value.trim())}
                      placeholder="Last name"
                      className="bg-zinc-800 border-zinc-700"
                      disabled={isSaving}
                    />
                  </div>
                  <Button
                    onClick={handleUpdateName}
                    disabled={!firstName.trim() || isSaving}
                    className="bg-green-600 hover:bg-green-700 px-2"
                  >
                    <Check className="h-5 w-5" />
                  </Button>
                </div>
              ) : (
                <div className="text-center text-xl font-bold">
                  {firstName} {lastName}
                </div>
              )}
            </div>

            {/* Bio section */}
            <div className="mb-6">
              <span className="text-xs text-zinc-400 mb-1">BIO</span>
              <AlertDialogDescription className="bg-gradient-to-br from-rose-800 to-purple-900 rounded-lg p-3 text-white text-sm">
                {character.aboutme || <span className="text-zinc-500">No bio available</span>}
              </AlertDialogDescription>
            </div>

            {/* Attribute sections */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <CharacterAttribute label="Personality" value={character.personality} />
              <CharacterAttribute label="Relationship" value={character.relationship} />
              <CharacterAttribute label="Occupation" value={character.occupation} />
              <CharacterAttribute label="Age" value={character.age} />
              <CharacterAttribute 
                label="Hobbies" 
                value={Array.isArray(character.hobbies) ? character.hobbies.join(', ') : character.hobbies}
                className="col-span-2" 
              />
            </div>

            {/* Action buttons */}
            <div className="mt-auto flex flex-col gap-3">
              <Button
                onClick={onStartChat}
                size="lg"
                className={`w-full py-6 bg-purple-800 hover:bg-purple-700 text-white font-bold`}
                disabled={imageStatus !== 'success'}
              >
                <MessageCircle className="h-4 w-4" />
                <span>Start Chatting</span>
              </Button>
              
              <Button
                onClick={() => onOpenChange(false)}
                size="lg"
                className="w-full py-2 bg-amber-600 hover:bg-amber-500"
              >
                <SquarePlus className="h-4 w-4" />
                <span>Save and Create New</span>
              </Button>
            </div>
          </div>
        </div>
        {showFlagDialog && (
          <EhFlagDialog
            open={showFlagDialog}
            onOpenChange={setShowFlagDialog}
            flaggedResourceId={character.id}
            onClose={(success) => {
              setShowFlagDialog(false);
              if (success) {
                onCharacterSave();
                onOpenChange(false);
              }
            }}
            thumbnailUrl={getHeaderImage()}
            flagData={{
              userAttributes,
            }}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}; 